import { Formik } from 'formik';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Field, Form, ModalCard } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { dateFormats, emptyStringToNull, ordinal } from '~/utils';
import { revenueTypes } from './revenueTypes';

const ContainerControl = styled.span`
  display: flex;
  align-items: center;
`;

export default function AddMultipleDialog({ currency, project, onSaved, onCancel }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();

  const initialValues = {
    start: null,
    end: null,
    dayOfMonth: null,
    revenueType: '',
    amount: '',
    notes: '',
  };

  const handleSubmit = async (values, formik) => {
    formik.setSubmitting(true);

    const { start, end, dayOfMonth, revenueType, amount, notes } = emptyStringToNull(values);

    const { data: dates } = await api.www
      .workspaces(workspace.id)
      .projects(project.id)
      .generateRecurringDates({ start, end, dayOfMonth });

    const instances = dates.map((date) => ({
      date: moment(date).format(dateFormats.isoDate),
      revenueType,
      amount,
      notes,
    }));

    try {
      await api.www.workspaces(workspace.id).projectSettings(project.id).revenueAdjustments().batch.create(instances);
      toast.success('Earned Revenue Adjustments added successfully.');
      await onSaved();
    } catch (error) {
      toast.error('Failed to add earned revenue adjustments.');
    }

    formik.setSubmitting(false);
  };

  return (
    <ModalCard title="Add Earned Revenue Adjustments" onClose={onCancel}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          start: Yup.date().label('Starting Month').nullable().required(),
          end: Yup.date()
            .label('Ending Month')
            .nullable()
            .min(Yup.ref('start'), 'Ending Month must be after Starting Month')
            .required(),
          dayOfMonth: Yup.mixed()
            .label('Day of Month')
            .oneOf([...Array(28).keys()].map((i) => i + 1).concat('last'))
            .required(),
          revenueType: Yup.string().label('Revenue Type').oneOf(Object.keys(revenueTypes)).required(),
          amount: Yup.number().label('Amount').min(-99999999999).max(99999999999).nullable().required(),
          notes: Yup.string().label('Notes').max(255),
        })}>
        {({ submitForm, ...formik }) => {
          return (
            <>
              <ModalCard.Body>
                <Form.Control>
                  <Field.DayPicker
                    name="start"
                    placeholder="Starting Month"
                    locale="en-US"
                    displayFormat={dateFormats.monthYear}
                    scope="month"
                  />
                  <Field.DayPicker
                    name="end"
                    placeholder="Ending Month"
                    locale="en-US"
                    displayFormat={dateFormats.monthYear}
                    scope="month"
                  />
                </Form.Control>

                <Form.Control>
                  <ContainerControl style={{ marginTop: '0' }}>
                    Scheduled on the
                    <div style={{ margin: '0 0.5rem' }}>
                      <Field.SingleSelect
                        name="dayOfMonth"
                        style={{
                          width: '7rem',
                          margin: '0 0.5rem',
                          textAlign: 'right',
                        }}
                        materialPlaceholder={false}>
                        {[...Array(28).keys()].map((day) => (
                          <option key={day} value={day + 1}>
                            {ordinal(day + 1)}
                          </option>
                        ))}
                        <option value="last">Last</option>
                      </Field.SingleSelect>
                    </div>
                    <span>day of each month</span>
                  </ContainerControl>
                </Form.Control>

                <Form.Control>
                  <Field.SingleSelect name="revenueType" placeholder="Revenue Type">
                    {Object.entries(revenueTypes).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Field.SingleSelect>

                  <Field.Currency name="amount" placeholder="Amount" currency={currency} />
                </Form.Control>

                <Form.Control>
                  <Field.Text name="notes" placeholder="Notes" maxLength={255} />
                </Form.Control>
              </ModalCard.Body>
              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={onCancel}>Cancel</CancelButton>
                  <Button isLoading={formik.isSubmitting} onClick={submitForm}>
                    Add
                  </Button>
                </Buttons>
              </ModalCard.Footer>
            </>
          );
        }}
      </Formik>
    </ModalCard>
  );
}
